import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Paper, IconButton, Tooltip, TablePagination } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import {
  AddIcon as AddBoxOutlined, CheckIcon as Check, CloseIcon as Clear,
  DeleteIcon as DeleteOutline, ChevronRightIcon as ChevronRight, EditIcon as EditOutlined,
  DownloadIcon as SaveAlt, FilterIcon as FilterList, FirstPageIcon as FirstPage,
  LastPageIcon as LastPage, ChevronLeftIcon as ChevronLeft, SearchIcon as Search,
  ArrowDownIcon as ArrowDownward, MinusIcon as Remove, ColumnsIcon as ViewColumn,
  ArchiveIcon as ArchiveOutlined, UnarchiveIcon as UnarchiveOutlined,
} from '../AdminIcon';

// SVG icons keep table actions readable without an external icon-font request.
const icon = Component => forwardRef((props, ref) => <Component {...props} ref={ref} />);
export const tableIcons = {
  Add: icon(AddBoxOutlined), Check: icon(Check), Clear: icon(Clear),
  Delete: icon(DeleteOutline), DetailPanel: icon(ChevronRight), Edit: icon(EditOutlined),
  Export: icon(SaveAlt), Filter: icon(FilterList), FirstPage: icon(FirstPage),
  LastPage: icon(LastPage), NextPage: icon(ChevronRight), PreviousPage: icon(ChevronLeft),
  ResetSearch: icon(Clear), Search: icon(Search), SortArrow: icon(ArrowDownward),
  ThirdStateCheck: icon(Remove), ViewColumn: icon(ViewColumn),
};
const actionIcons = { archive: ArchiveOutlined, unarchive: UnarchiveOutlined };
export function presentTableAction(action) {
  if (typeof action === 'function') return row => presentTableAction(action(row));
  const Icon = actionIcons[action.icon];
  return Icon ? { ...action, icon: () => <Icon /> } : action;
}
export const tableOptions = {
  padding: 'dense', actionsColumnIndex: -1, emptyRowsWhenPaging: false, draggable: false,
  searchFieldVariant: 'outlined', searchFieldStyle: { minWidth: 160 },
};
function TableSurface({ children, ...props }) {
  const surfaceRef = useRef(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    // MaterialTable owns this scroll container; enhance its keyboard access at
    // the supported Container boundary without replacing its table workflow.
    const wrapper = surfaceRef.current?.querySelector('[class*="horizontalScrollContainer"]');
    // Its vertical-scroll child also owns horizontal overflow in this version.
    const region = wrapper?.querySelector('div[style*="overflow-y: auto"]') || wrapper;
    if (!region) return undefined;
    const update = () => {
      surfaceRef.current?.style.setProperty('--admin-table-viewport-width', `${region.clientWidth}px`);
      const overflow = region.scrollWidth > region.clientWidth + 1;
      setScrollable(overflow);
      if (overflow) {
        region.tabIndex = 0;
        region.setAttribute('role', 'region');
        region.setAttribute('aria-label', 'Table columns; scroll horizontally to see more');
      } else {
        region.removeAttribute('tabindex');
        region.removeAttribute('role');
        region.removeAttribute('aria-label');
      }
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(region);
    const table = region.querySelector('table');
    if (table) observer?.observe(table);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); };
  }, [children]);
  return <Paper {...props} ref={surfaceRef} elevation={0} className="admin-data-table">
    {children}
    {scrollable && <p className="admin-table-scroll-hint">Scroll horizontally for more columns.</p>}
  </Paper>;
}
function TableAction({ action, data, disabled, size }) {
  let resolved = typeof action === 'function' ? action(data) : action;
  if (resolved?.action) resolved = resolved.action(data);
  if (!resolved || resolved.hidden) return null;
  const isDisabled = disabled || resolved.disabled;
  const Icon = resolved.icon;
  const glyph = typeof Icon === 'function' ? Icon({ ...resolved.iconProps, disabled: isDisabled }) :
    typeof Icon === 'string' ? Icon : <Icon />;
  return <Tooltip title={resolved.tooltip || ''}><span>
    <IconButton size={size} color="inherit" disabled={isDisabled} aria-label={resolved.tooltip}
      onClick={event => { resolved.onClick?.(event, data); event.stopPropagation(); }}>{glyph}</IconButton>
  </span></Tooltip>;
}
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const actions = [
    ['First page', FirstPage, 0, page === 0],
    ['Previous page', ChevronLeft, page - 1, page === 0],
    ['Next page', ChevronRight, page + 1, page >= lastPage],
    ['Last page', LastPage, lastPage, page >= lastPage],
  ];
  return <div className="admin-table-pagination-actions">{actions.map(([label, Icon, target, disabled]) =>
    <IconButton key={label} aria-label={label} title={label} disabled={disabled} onClick={event => onPageChange(event, target)}><Icon /></IconButton>
  )}</div>;
}
function AccessibleTablePagination({ icons, onChangePage, onChangeRowsPerPage, onPageChange, onRowsPerPageChange, ...props }) {
  // MaterialTable still supplies the legacy callbacks; keep that compatibility
  // at this boundary instead of passing deprecated props into Material UI.
  return <TablePagination {...props}
    onPageChange={onPageChange || onChangePage}
    onRowsPerPageChange={onRowsPerPageChange || onChangeRowsPerPage}
    SelectProps={{ ...props.SelectProps, inputProps: { ...props.SelectProps?.inputProps, 'aria-label': 'Rows per page' } }}
    ActionsComponent={TablePaginationActions} />;
}
function TableLoadingOverlay({ theme }) {
  // Preserve the existing loading surface without MaterialTable's deprecated
  // fade helper. The supported component slot also lets us name its progress.
  return <Box display="table" width="100%" height="100%" bgcolor={alpha(theme.palette.background.paper, 0.7)}>
    <Box display="table-cell" width="100%" height="100%" style={{ verticalAlign: 'middle', textAlign: 'center' }}>
      <CircularProgress aria-label="Loading records" />
    </Box>
  </Box>;
}
export const tableComponents = { Container: TableSurface, Action: TableAction, Pagination: AccessibleTablePagination, OverlayLoading: TableLoadingOverlay };
